import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Recommendation, RecommendationLever, RecommendationsResponse } from '../common/process-recommendations';
import { RecommendationSource, RecommendationsService } from './recommendations.service';
import { Suggestion } from './simulation-report';

export interface RecommendationsDialogData {
    source: RecommendationSource;
    canTest: boolean;                   // recommendations can be tried in a project from here
    testDisabledReason: string;
}

// what the dialog closes with: the recommendations' changes to try in a project, if any were chosen
export interface RecommendationsDialogResult {
    test?: Suggestion[];
}

const LEVER_LABELS: Record<RecommendationLever, string> = {
    eliminate: 'Eliminate',
    simplify: 'Simplify',
    rebalance: 'Rebalance',
    automate: 'Automate',
    ai: 'Use AI',
    capacity: 'Add capacity',
    model: 'Check the model',
};

// overall recommendations from AI, from the EPM scorecard's evidence and the simulation's, most valuable first. Those
// that can be modelled can be tried together in a new project
@Component({
    selector: 'app-recommendations-dialog',
    standalone: true,
    imports: [FormsModule, MatButtonModule, MatCheckboxModule, MatDialogModule, MatIconModule, MatProgressBarModule, MatTooltipModule],
    template: `
        <h2 mat-dialog-title>Recommendations</h2>
        <mat-dialog-content>
            @if (loading) {
            <p role="status" data-testid="recommendations-progress">Working out recommendations from the scorecard and the simulation…</p>
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
            }@else if (error) {
            <p class="error" role="alert">{{ error }}</p>
            }@else if (response) {
            <p class="when" data-testid="recommendations-when">{{ held ? 'Worked out earlier, at ' : 'Worked out at ' }}{{ timeOf(at) }} from this simulation.</p>
            <p class="summary" data-testid="recommendations-summary">{{ response.summary }}</p>
            <ol class="recommendations" data-testid="recommendations">
                @for (r of response.recommendations; track $index) {
                <li class="recommendation">
                    <header>
                        @if (r.changes.length) {
                        <mat-checkbox [(ngModel)]="chosen[$index]" [disabled]="!data.canTest" [attr.data-testid]="'recommendation-choice-' + $index"
                            [matTooltip]="data.canTest ? 'Try its changes in a new project' : data.testDisabledReason"></mat-checkbox>
                        }
                        <h3>{{ r.title }}</h3>
                    </header>
                    <div class="chips">
                        <span class="chip lever" [class]="'chip lever ' + r.lever">{{ leverLabel(r) }}</span>
                        <span class="chip">Impact {{ r.impact }}/10</span>
                        <span class="chip">{{ r.effort }} effort</span>
                        <span class="chip">{{ r.confidence }} confidence</span>
                    </div>
                    <dl>
                        <dt>Problem</dt><dd>{{ r.problem }}</dd>
                        <dt>Evidence</dt><dd>{{ r.evidence }}</dd>
                        <dt>What to do</dt><dd>{{ r.action }}</dd>
                        <dt>Expected effect</dt><dd>{{ r.expectedEffect }}</dd>
                        @if (r.steps.length) {<dt>Steps</dt><dd>{{ r.steps.join(', ') }}</dd>}
                    </dl>
                    @if (r.changes.length || r.rejected.length) {
                    <ul class="changes">
                        @for (c of r.changes; track $index) {<li><mat-icon>build</mat-icon>{{ c.description }}</li>}
                        @for (c of r.rejected; track $index) {<li class="rejected"><mat-icon>block</mat-icon>{{ c.description }} ({{ c.reason }})</li>}
                    </ul>
                    }
                </li>
                }
            </ol>
            <p class="note">AI works from the model's figures and may be wrong. Test changes in a project, and check the figures for
                the steps involved, before acting on them.</p>
            }
        </mat-dialog-content>
        <mat-dialog-actions align="end">
            @if (error) {<button mat-button (click)="load()">Try again</button>}
            @if (response && !loading) {<button mat-button (click)="load(true)" data-testid="recommendations-again" matTooltip="Ask AI for a fresh set of recommendations">Ask AI again</button>}
            <button mat-button mat-dialog-close>Close</button>
            @if (response && testable) {
            <button mat-flat-button color="primary" [disabled]="!anyChosen || !data.canTest" (click)="test()" data-testid="test-recommendations"
                [matTooltip]="data.canTest ? '' : data.testDisabledReason">Test the chosen in a project</button>
            }
        </mat-dialog-actions>
    `,
    styles: [`
        .when { margin: 0 0 4px; font-size: 12px; color: var(--slate, #5b6172); }
        .summary { font-size: 14px; }
        .recommendations { margin: 0; padding: 0 0 0 20px; }
        .recommendation { padding: 10px 0; border-top: 1px solid rgba(0,0,0,.1); }
        header { display: flex; align-items: center; gap: 4px; }
        h3 { margin: 0; font-size: 15px; }
        .chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 6px 0; }
        .chip { padding: 1px 8px; border-radius: 10px; background: #eef2f7; color: #334155; font-size: 12px; }
        .chip.ai { background: #ede9fe; color: #5b21b6; }
        .chip.automate { background: #e0f2fe; color: #075985; }
        .chip.eliminate, .chip.simplify { background: #dcfce7; color: #166534; }
        .chip.capacity, .chip.rebalance { background: #fef3c7; color: #92400e; }
        .chip.model { background: #fee2e2; color: #991b1b; }
        dl { display: grid; grid-template-columns: max-content 1fr; gap: 2px 12px; margin: 4px 0; font-size: 13px; }
        dt { color: var(--slate, #5b6172); }
        dd { margin: 0; overflow-wrap: anywhere; }
        .changes { list-style: none; margin: 6px 0 0; padding: 0; font-size: 13px; }
        .changes li { display: flex; align-items: flex-start; gap: 6px; padding: 2px 0; }
        .changes mat-icon { font-size: 16px; width: 16px; height: 16px; flex: none; color: #0f766e; }
        .changes .rejected { color: var(--slate, #5b6172); }
        .changes .rejected mat-icon { color: #b91c1c; }
        .note { font-size: 12px; color: var(--slate, #5b6172); }
        .error { color: #b91c1c; }
        @media (max-width: 600px) { dl { grid-template-columns: 1fr; } }
    `],
})
export class RecommendationsDialogComponent implements OnInit {
    loading = false;
    error = '';
    response?: RecommendationsResponse;
    at?: Date;                  // when the recommendations were worked out
    held = false;               // they were held from an earlier request, rather than just worked out
    chosen: boolean[] = [];

    constructor(@Inject(MAT_DIALOG_DATA) public data: RecommendationsDialogData,
        private dialogRef: MatDialogRef<RecommendationsDialogComponent, RecommendationsDialogResult>, private recommendations: RecommendationsService) {}

    ngOnInit() {
        void this.load();
    }

    // the recommendations, held from earlier for the same simulation unless asked for afresh
    async load(afresh = false) {
        this.loading = true;
        this.error = '';
        try {
            const result = await this.recommendations.recommend(this.data.source, afresh);
            this.response = result.response;
            this.at = result.at;
            this.held = result.held;
            // the first that can be modelled is chosen to start with, as it's the most valuable
            const first = this.response.recommendations.findIndex(r => r.changes.length > 0);
            this.chosen = this.response.recommendations.map((_, i) => i === first);
        }catch(err: any) {
            console.error('Unable to work out recommendations', err);
            this.error = "The recommendations couldn't be worked out. Please try again.";
        }finally {
            this.loading = false;
        }
    }

    timeOf(at?: Date) {
        return at ? at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
    }

    get testable() {
        return !!this.response?.recommendations.some(r => r.changes.length);
    }

    get anyChosen() {
        return this.chosen.some(c => c);
    }

    leverLabel(r: Recommendation) {
        return LEVER_LABELS[r.lever] || r.lever;
    }

    // each chosen recommendation's changes, to be tried together in a project
    test() {
        const suggestions = (this.response?.recommendations || []).filter((r, i) => this.chosen[i] && r.changes.length)
            .flatMap(r => r.changes.map(change => ({ text: r.title, change }) as Suggestion));
        this.dialogRef.close({ test: suggestions });
    }
}
